/*
 * Wooded V2 — Testimonials masonry
 *
 * Packs .wc-testimonials__grid into columns of uneven height. Progressive
 * enhancement: without this file the grid stays a plain CSS grid, which is
 * correct but leaves a hole under every card shorter than its row.
 *
 * Why not CSS. `grid-template-rows: masonry` is not shipping anywhere we can
 * rely on. Multi-column comes close, but its balancer cannot split a card, and
 * with heights this uneven — a square paired photo against a pull quote — it
 * strands whole columns: three columns here left the last one holding a single
 * card with 680px of nothing beneath it.
 *
 * Placement runs in two parts. The first row is dealt left to right so the
 * newest testimonials still read across the top in order. The rest are fitted
 * by search rather than by the usual greedy shortest-column rule, which is
 * short-sighted at this size: greedy left the tallest column 380px longer than
 * the shortest, where choosing the whole tail at once gets that under 170px.
 * The search is exhaustive, so it is capped — past the cap, greedy.
 *
 * Columns also start at staggered heights, which breaks the flat top edge
 * three equal columns would otherwise share. The offsets are seeded into the
 * running heights before placement, so the balancer treats them as content and
 * the bottoms stay level; a stagger applied afterwards would just tip the
 * whole layout sideways.
 */
(function () {
  var GRID = '.wc-testimonials__grid';
  var SEARCH_CAP = 9; // columns^this, evaluated once per layout

  /* Deliberately uneven — matched offsets read as a second, tidier grid. */
  var STAGGER = { 2: [0, 26], 3: [0, 38, 16] };

  function stagger(cols) {
    var s = STAGGER[cols];
    if (s) return s.slice();
    var out = [];
    for (var i = 0; i < cols; i++) out.push(0);
    return out;
  }

  function colCount(grid) {
    var v = parseInt(getComputedStyle(grid).getPropertyValue('--wc-cols'), 10);
    return v > 0 ? v : 1;
  }

  function teardown(grid, cards) {
    grid.classList.remove('is-masonry');
    grid.style.height = '';
    cards.forEach(function (card) {
      card.style.width = '';
      card.style.left = '';
      card.style.top = '';
    });
  }

  /* Where each remaining card goes, as column indices. Minimises the tallest
     column first, then the spread, so the bottom edge comes out level. */
  function assign(heights, tail, cols, gap) {
    var best = null;
    var bestKey = null;

    function score(cand) {
      var h = heights.slice();
      for (var i = 0; i < cand.length; i++) h[cand[i]] += tail[i] + gap;
      var max = Math.max.apply(null, h);
      var min = Math.min.apply(null, h);
      return [max, max - min];
    }

    if (Math.pow(cols, tail.length) <= Math.pow(cols, SEARCH_CAP)
        && tail.length <= SEARCH_CAP) {
      var total = Math.pow(cols, tail.length);
      for (var n = 0; n < total; n++) {
        var cand = [];
        var q = n;
        for (var i = 0; i < tail.length; i++) { cand.push(q % cols); q = Math.floor(q / cols); }
        var key = score(cand);
        if (!bestKey || key[0] < bestKey[0] || (key[0] === bestKey[0] && key[1] < bestKey[1])) {
          bestKey = key;
          best = cand;
        }
      }
      return best;
    }

    // Fallback: greedy shortest-column.
    var h = heights.slice();
    return tail.map(function (cardH) {
      var t = 0;
      for (var i = 1; i < cols; i++) if (h[i] < h[t]) t = i;
      h[t] += cardH + gap;
      return t;
    });
  }

  function layout(grid) {
    var cards = Array.prototype.slice.call(grid.children);
    if (!cards.length) return;

    var cols = colCount(grid);
    if (cols < 2) return teardown(grid, cards);

    var cs = getComputedStyle(grid);
    var gap = parseFloat(cs.columnGap) || 22;

    grid.classList.add('is-masonry');
    var colW = (grid.clientWidth - gap * (cols - 1)) / cols;

    // Width first, heights second: a card's height depends on the width it is
    // given, so every measurement below has to happen after this loop.
    cards.forEach(function (card) { card.style.width = colW + 'px'; });
    var natural = cards.map(function (card) { return card.offsetHeight; });

    var offsets = stagger(cols);
    var heights = offsets.slice();
    var placement = [];
    var i;

    var lead = Math.min(cols, cards.length);
    for (i = 0; i < lead; i++) {
      placement.push(i);
      heights[i] = offsets[i] + natural[i] + gap;
    }
    if (cards.length > lead) {
      var tail = assign(heights, natural.slice(lead), cols, gap);
      for (i = 0; i < tail.length; i++) {
        placement.push(tail[i]);
        heights[tail[i]] += natural[lead + i] + gap;
      }
    }

    var running = offsets.slice();
    cards.forEach(function (card, idx) {
      var col = placement[idx];
      card.style.left = col * (colW + gap) + 'px';
      card.style.top = running[col] + 'px';
      running[col] += natural[idx] + gap;
    });

    grid.style.height = Math.max.apply(null, running) - gap + 'px';
  }

  function layoutAll() {
    document.querySelectorAll(GRID).forEach(layout);
  }

  function schedule() {
    window.requestAnimationFrame(layoutAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  // Photos land after first paint and change every height below them; web
  // fonts do the same to the quotes.
  window.addEventListener('load', schedule);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
  document.querySelectorAll(GRID + ' img').forEach(function (img) {
    if (!img.complete) img.addEventListener('load', schedule, { once: true });
  });

  var t;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(schedule, 120);
  });
})();
